import { useState } from 'react';
import type { PlanBlock } from '../api/bindings/PlanBlock.ts';
import {
  useApplyGeneration,
  useDiscardGeneration,
  useGeneration,
  useStartGeneration,
} from '../api/hooks/useGeneration.ts';
import { BlockView } from './blocks/index.tsx';

/**
 * The generate → review → apply/discard cycle for one block. Rendered inside the
 * editor's block card; polling runs while a generation is pending.
 */
export default function GenerationReview({
  planId,
  block,
}: {
  planId: string;
  block: PlanBlock;
}) {
  const [generationId, setGenerationId] = useState<string>();
  const start = useStartGeneration(planId);
  const generation = useGeneration(planId, generationId);
  const apply = useApplyGeneration(planId);
  const discard = useDiscardGeneration(planId);

  const gen = generation.data?.generation;
  const busy = start.isPending || gen?.status === 'pending';

  const startGeneration = () =>
    start.mutate(block.id, { onSuccess: (res) => setGenerationId(res.generation_id) });

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      {!generationId && (
        <button
          type="button"
          className="rounded-md bg-violet-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          disabled={busy}
          onClick={startGeneration}
        >
          {start.isPending ? 'Starting…' : '✨ Draft with AI'}
        </button>
      )}
      {start.isError && (
        <p className="mt-2 text-sm text-red-600">Could not start: {start.error.message}</p>
      )}

      {gen?.status === 'pending' && (
        <p className="text-sm text-violet-600">
          <span className="mr-2 inline-block animate-pulse">✨</span>
          Drafting… this can take a minute on the local models.
        </p>
      )}

      {gen?.status === 'failed' && (
        <div className="rounded-md bg-red-50 p-3">
          <p className="text-sm text-red-700">Draft failed: {gen.error ?? 'unknown error'}</p>
          <button
            type="button"
            className="mt-1 text-sm font-semibold text-red-700 hover:underline"
            onClick={startGeneration}
          >
            Try again
          </button>
        </div>
      )}

      {gen?.status === 'succeeded' && gen.proposed_content && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-violet-700">
              Proposed draft {gen.model && <span className="font-normal text-violet-400">({gen.model})</span>}
            </span>
            <div className="flex gap-3">
              <button
                type="button"
                className="text-sm font-semibold text-violet-700 hover:underline"
                disabled={apply.isPending}
                onClick={() =>
                  apply.mutate(gen.id, { onSuccess: () => setGenerationId(undefined) })
                }
              >
                {apply.isPending ? 'Applying…' : 'Apply'}
              </button>
              <button
                type="button"
                className="text-sm text-gray-500 hover:underline"
                onClick={() => {
                  discard.mutate(gen.id);
                  setGenerationId(undefined);
                }}
              >
                Discard
              </button>
              <button
                type="button"
                className="text-sm text-gray-500 hover:underline"
                onClick={() => {
                  discard.mutate(gen.id);
                  startGeneration();
                }}
              >
                Regenerate
              </button>
            </div>
          </div>
          <div className="rounded-md bg-white p-3">
            <BlockView content={gen.proposed_content} />
          </div>
          {apply.isError && (
            <p className="mt-2 text-sm text-red-600">Apply failed: {apply.error.message}</p>
          )}
        </div>
      )}

      {(gen?.status === 'applied' || gen?.status === 'discarded') && generationId && (
        <button
          type="button"
          className="text-sm text-violet-600 hover:underline"
          onClick={startGeneration}
        >
          ✨ Draft again
        </button>
      )}
    </div>
  );
}
